// src/components/home/HeroSection.tsx
import React, { useLayoutEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowUpRight, Sparkles, ShoppingBag } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

export type HeroProps = {
  title?: string; // có thể xuống dòng bằng "\n"
  subtitle?: string;
  description?: string;
  imageUrl: string;

  ctaTo?: string;
  ctaLabel?: string;

  // ✅ CTA phụ
  secondaryTo?: string;
  secondaryLabel?: string;

  // ✅ optional: link lookbook ở panel dưới (để reuse)
  lookbookTo?: string;
  lookbookLabel?: string;

  // ✅ optional: override các text nhỏ
  microLeft?: string;
  microRight?: string;
  cornerLabel?: string;
  panelKicker?: string;
  panelDesc?: string;
  panelTagline?: string;
  scrollLabel?: string;
};

function cn(...x: Array<string | false | undefined | null>) {
  return x.filter(Boolean).join(" ");
}

/* ======================
   SPLIT HELPERS
====================== */
function splitLines(input: string) {
  return input
    .split(/\n|<br\s*\/?>/gi)
    .map((s) => s.trim())
    .filter(Boolean);
}

function splitWords(line: string) {
  return line.split(" ").filter(Boolean);
}

function usePrefersReducedMotion() {
  return useMemo(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;
  }, []);
}

export default function HeroSection(props: HeroProps) {
  const { t } = useTranslation();

  const {
    imageUrl,

    // main texts: ưu tiên props, fallback i18n
    title = t("hero.title"),
    subtitle = t("hero.subtitle"),
    description = t("hero.description"),

    // primary CTA
    ctaTo = "/drops/current",
    ctaLabel = t("hero.ctaPrimary"),

    // secondary CTA
    secondaryTo = "/collection",
    secondaryLabel = t("hero.ctaSecondary"),

    // bottom panel lookbook
    lookbookTo = "/lookbook",
    lookbookLabel = t("hero.lookbookCta"),

    // micro line
    microLeft = t("hero.micro.left"),
    microRight = t("hero.micro.right"),

    // corner label
    cornerLabel = t("hero.cornerLabel"),

    // bottom panel text
    panelKicker = t("hero.panel.kicker"),
    panelDesc = t("hero.panel.desc"),
    panelTagline = t("hero.panel.tagline"),

    // scroll indicator
    scrollLabel = t("hero.scroll"),
  } = props;

  const rootRef = useRef<HTMLElement | null>(null);
  const titleRef = useRef<HTMLHeadingElement | null>(null);
  const descRef = useRef<HTMLParagraphElement | null>(null);
  const ctaRef = useRef<HTMLDivElement | null>(null);
  const mediaRef = useRef<HTMLDivElement | null>(null);

  const reducedMotion = usePrefersReducedMotion();

  // ✅ pre-split 1 lần
  const lines = useMemo(() => splitLines(title), [title]);
  const wordsByLine = useMemo(() => lines.map(splitWords), [lines]);

  useLayoutEffect(() => {
    if (!rootRef.current) return;
    if (reducedMotion) return;

    const ctx = gsap.context(() => {
      const words = titleRef.current
        ? Array.from(
            titleRef.current.querySelectorAll<HTMLElement>("[data-od-word]"),
          )
        : [];

      gsap.set(words, { y: 16, opacity: 0 });
      if (descRef.current) gsap.set(descRef.current, { y: 12, opacity: 0 });
      if (ctaRef.current) gsap.set(ctaRef.current, { y: 10, opacity: 0 });
      if (mediaRef.current) gsap.set(mediaRef.current, { y: 14, opacity: 0 });

      gsap
        .timeline({
          defaults: { ease: "power3.out" },
          scrollTrigger: {
            trigger: rootRef.current,
            start: "top 70%",
            once: true,
          },
        })
        .to(words, {
          y: 0,
          opacity: 1,
          duration: 0.8,
          stagger: 0.018,
        })
        .to(descRef.current, { y: 0, opacity: 1, duration: 0.65 }, "-=0.35")
        .to(ctaRef.current, { y: 0, opacity: 1, duration: 0.55 }, "-=0.35")
        .to(mediaRef.current, { y: 0, opacity: 1, duration: 0.65 }, "-=0.55");
    }, rootRef);

    return () => ctx.revert();
  }, [reducedMotion, wordsByLine]);

  return (
    <section
      ref={rootRef}
      className="relative min-h-[92vh] lg:min-h-[96vh] bg-brand-ivory text-brand-brown overflow-hidden"
    >
      {/* ===== BACKGROUND WASH ===== */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-b from-brand-ivory via-brand-ivory to-brand-ivory" />
        <div className="absolute inset-0 bg-gradient-to-tr from-brand-gold/12 via-transparent to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-bl from-brand-brown/8 via-transparent to-transparent" />
      </div>

      {/* ===== TOP FADE LINE ===== */}
      <div className="pointer-events-none absolute top-0 left-0 right-0 h-px bg-brand-brown/10" />

      {/* ===== CONTENT WRAP ===== */}
      <div className="relative z-10 mx-auto max-w-7xl px-6 sm:px-8 lg:px-16 pt-28 lg:pt-32 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
          {/* ================= LEFT ================= */}
          <div className="lg:col-span-6">
            {/* kicker */}
            <div className="inline-flex items-center gap-3">
              <span className="h-9 w-9 rounded-2xl border border-brand-brown/10 bg-brand-brown/5 grid place-items-center">
                <Sparkles className="w-4 h-4 text-brand-gold" />
              </span>

              <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                {subtitle}
              </p>
            </div>

            {/* title */}
            <h1
              ref={titleRef}
              className="mt-10 text-[40px] sm:text-[52px] lg:text-[62px] leading-[1.12] uppercase tracking-[0.22em]"
            >
              {wordsByLine.map((words, li) => (
                <span
                  key={li}
                  className="block overflow-hidden pt-3 -mt-3 pb-2"
                >
                  <span className="inline-block">
                    {words.map((w, wi) => (
                      <span key={wi} data-od-word className="inline-block">
                        {w}
                        {wi < words.length - 1 ? "\u00A0" : ""}
                      </span>
                    ))}
                  </span>
                </span>
              ))}
            </h1>

            {/* desc */}
            <p
              ref={descRef}
              className="mt-8 max-w-xl text-sm sm:text-base leading-[2.0] text-brand-brown/60"
            >
              {description}
            </p>

            {/* CTAs */}
            <div
              ref={ctaRef}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              {/* Primary */}
              <Link
                to={ctaTo}
                className="inline-flex items-center gap-3 h-12 px-7 rounded-full
                           bg-brand-brown text-brand-ivory
                           text-[10px] uppercase tracking-[0.55em]
                           hover:opacity-95 transition"
              >
                <ArrowUpRight className="w-4 h-4" />
                {ctaLabel}
              </Link>

              {/* Secondary */}
              <Link
                to={secondaryTo}
                className="inline-flex items-center gap-3 h-12 px-7 rounded-full
                           border border-brand-brown/12 bg-transparent
                           text-[10px] uppercase tracking-[0.55em] text-brand-brown/70
                           hover:border-brand-brown/25 hover:text-brand-brown hover:bg-brand-brown/5 transition"
              >
                <ShoppingBag className="w-4 h-4" />
                {secondaryLabel}
              </Link>
            </div>

            {/* micro line */}
            <div className="mt-10 flex items-center gap-4 text-brand-brown/45">
              <span className="text-[10px] uppercase tracking-[0.55em]">
                {microLeft}
              </span>
              <span className="h-px w-16 bg-brand-brown/20" />
              <span className="text-[10px] uppercase tracking-[0.55em]">
                {microRight}
              </span>
            </div>
          </div>

          {/* ================= RIGHT: IMAGE CARD ================= */}
          <div className="lg:col-span-6">
            <div
              ref={mediaRef}
              className="relative rounded-[34px] border border-brand-brown/10 bg-brand-ivory/70 backdrop-blur-md
                         shadow-[0_40px_120px_rgba(43,31,26,0.10)] overflow-hidden"
            >
              {/* image */}
              <div className="relative">
                <img
                  src={imageUrl}
                  alt={subtitle}
                  className="w-full h-[clamp(320px,55vh,640px)] object-cover select-none pointer-events-none"
                  draggable={false}
                  loading="eager"
                  fetchPriority="high"
                />

                {/* overlay wash */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-brand-brown/25 via-transparent to-brand-ivory/10" />

                {/* corner label */}
                <div className="absolute top-5 left-5 rounded-full bg-brand-ivory/75 backdrop-blur-md border border-brand-brown/10 px-4 py-2">
                  <span className="text-[9px] uppercase tracking-[0.48em] text-brand-brown/70">
                    {cornerLabel}
                  </span>
                </div>
              </div>

              {/* bottom panel */}
              <div className="px-6 sm:px-8 py-6">
                <p className="text-[10px] uppercase tracking-[0.62em] text-brand-brown/55">
                  {panelKicker}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-brand-brown/60">
                  {panelDesc}
                </p>

                <div className="mt-5 flex items-center justify-between gap-4">
                  <span className="text-[10px] uppercase tracking-[0.55em] text-brand-brown/45">
                    {panelTagline}
                  </span>

                  {/* <Link
                    to={lookbookTo}
                    className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.55em]
                               text-brand-brown/55 hover:text-brand-brown transition"
                  >
                    {lookbookLabel} <ArrowUpRight className="w-4 h-4" />
                  </Link> */}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ===== SCROLL INDICATOR ===== */}
        <div className="mt-14 flex items-center justify-center">
          <div className="flex flex-col items-center gap-3 text-brand-brown/35">
            <span className="text-[10px] uppercase tracking-[0.55em]">
              {scrollLabel}
            </span>
            <span className="block h-10 w-px bg-brand-brown/20" />
          </div>
        </div>
      </div>
    </section>
  );
}
